import { loadScores, type ScoreCategory } from "../game/storage";
import { requireElement as element } from "./dom";

/**
 * Dialog helpers built on the native <dialog> element, which traps focus and closes on Escape.
 * Each helper resolves when the dialog closes.
 */

const PLAYER_NAME_KEY = "gweled.player-name";
const DEFAULT_PLAYER_NAME = "Player";

function whenClosed(dialog: HTMLDialogElement): Promise<void> {
  return new Promise((resolve) => {
    dialog.addEventListener("close", () => resolve(), { once: true });
  });
}

/** Asks a yes/no question. "No" has focus by default, like the original. */
export function confirmDialog(message: string): Promise<boolean> {
  const dialog = element<HTMLDialogElement>("confirm-dialog");
  element("confirm-message").textContent = message;
  dialog.returnValue = "";
  const closed = whenClosed(dialog);
  dialog.showModal();
  return closed.then(() => dialog.returnValue === "yes");
}

/** Asks for the name to store with a new high score. Escape keeps the default name. */
export function promptPlayerName(score: number): Promise<string> {
  const dialog = element<HTMLDialogElement>("name-dialog");
  const input = element<HTMLInputElement>("name-input");
  element("name-score").textContent = `Your score of ${score} made the top ten.`;
  input.value = readStoredName();
  const closed = whenClosed(dialog);
  dialog.showModal();
  input.focus();
  input.select();
  return closed.then(() => {
    const name = input.value.trim() || DEFAULT_PLAYER_NAME;
    storeName(name);
    return name;
  });
}

function readStoredName(): string {
  try {
    return window.localStorage.getItem(PLAYER_NAME_KEY) ?? DEFAULT_PLAYER_NAME;
  } catch {
    return DEFAULT_PLAYER_NAME;
  }
}

function storeName(name: string): void {
  try {
    window.localStorage.setItem(PLAYER_NAME_KEY, name);
  } catch {
    // Storage is optional.
  }
}

function formatDate(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString();
}

/** Shows the high score table. `highlight` marks a row that was just added. */
export function showScores(initial: ScoreCategory, highlight?: number): Promise<void> {
  const dialog = element<HTMLDialogElement>("scores-dialog");
  const select = element<HTMLSelectElement>("scores-category");
  const body = element("scores-body");
  const empty = element("scores-empty");
  const tables = loadScores();

  const render = (): void => {
    const category = select.value as ScoreCategory;
    const entries = tables[category];
    body.replaceChildren(
      ...entries.map((entry, index) => {
        const row = document.createElement("tr");
        if (category === initial && index === highlight) row.className = "highlight";
        const cells = [String(index + 1), entry.name, String(entry.score), formatDate(entry.date)];
        cells.forEach((text, column) => {
          const cell = document.createElement("td");
          cell.textContent = text;
          if (column === 2) cell.className = "num";
          row.append(cell);
        });
        return row;
      }),
    );
    empty.hidden = entries.length > 0;
  };

  select.value = initial;
  render();
  const onChange = (): void => render();
  select.addEventListener("change", onChange);
  const closed = whenClosed(dialog);
  dialog.showModal();
  return closed.then(() => {
    select.removeEventListener("change", onChange);
  });
}

export function showAbout(): Promise<void> {
  const dialog = element<HTMLDialogElement>("about-dialog");
  const closed = whenClosed(dialog);
  dialog.showModal();
  return closed;
}
